<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Populations</h2>
        <p class="text-sm text-gray-500">Define what a row represents: patients, discharges, visits, etc.</p>
      </div>
      <button
        @click="showEditor = true; selectedPopulation = null; resetForm()"
        class="btn-primary-sm"
      >
        + New Population
      </button>
    </div>

    <!-- Population List -->
    <div class="border border-gray-200 rounded">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-2 text-left font-medium text-gray-700">Name</th>
            <th class="px-4 py-2 text-left font-medium text-gray-700">Unit Type</th>
            <th class="px-4 py-2 text-left font-medium text-gray-700">Context Columns</th>
            <th class="px-4 py-2 text-center font-medium text-gray-700">Built-in</th>
            <th class="px-4 py-2 text-right font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="pop in populations"
            :key="pop.id"
            class="border-t border-gray-100 hover:bg-gray-50"
          >
            <td class="px-4 py-3">
              <div class="font-medium text-gray-900">{{ pop.name }}</div>
              <div class="text-xs text-gray-500 truncate max-w-md">{{ pop.description }}</div>
            </td>
            <td class="px-4 py-3">
              <span class="px-2 py-0.5 bg-gray-100 rounded text-xs">{{ pop.unit_type }}</span>
            </td>
            <td class="px-4 py-3 text-gray-600">
              <span v-for="(col, i) in pop.context_columns" :key="col" class="text-xs">
                {{ col }}<span v-if="i < pop.context_columns.length - 1">, </span>
              </span>
            </td>
            <td class="px-4 py-3 text-center">
              <span v-if="pop.is_builtin" class="text-blue-600">✓</span>
              <span v-else class="text-gray-300">-</span>
            </td>
            <td class="px-4 py-3 text-right">
              <button
                @click="editPopulation(pop)"
                class="text-blue-600 hover:text-blue-800 text-sm mr-3"
              >
                {{ pop.is_builtin ? 'View' : 'Edit' }}
              </button>
              <button
                v-if="!pop.is_builtin"
                @click="deletePopulation(pop)"
                class="text-red-600 hover:text-red-800 text-sm"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="populations.length === 0" class="px-4 py-8 text-center text-gray-500">
        No populations defined yet
      </div>
    </div>

    <!-- Editor Modal -->
    <div v-if="showEditor" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-semibold">
            {{ selectedPopulation ? (selectedPopulation.is_builtin ? 'View Population' : 'Edit Population') : 'New Population' }}
          </h2>
          <button @click="showEditor = false" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="px-6 py-4 space-y-4">
          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              v-model="form.name"
              type="text"
              :disabled="selectedPopulation?.is_builtin"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
              placeholder="e.g., IP_DISCHARGES"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="form.description"
              rows="2"
              :disabled="selectedPopulation?.is_builtin"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
              placeholder="What does this population represent?"
            ></textarea>
          </div>

          <!-- Unit Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Unit Type</label>
            <select
              v-model="form.unit_type"
              :disabled="selectedPopulation?.is_builtin"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
            >
              <option value="patient">Patient</option>
              <option value="discharge">Discharge</option>
              <option value="visit">Visit</option>
              <option value="episode">Episode</option>
            </select>
          </div>

          <!-- Context Columns -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Context Columns</label>
            <div class="flex flex-wrap gap-1 mb-2">
              <span
                v-for="col in form.context_columns"
                :key="col"
                class="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs"
              >
                {{ col }}
                <button
                  v-if="!selectedPopulation?.is_builtin"
                  @click="removeContextColumn(col)"
                  class="text-gray-400 hover:text-red-500"
                >×</button>
              </span>
            </div>
            <div v-if="!selectedPopulation?.is_builtin" class="flex gap-2">
              <input
                v-model="newContextColumn"
                type="text"
                class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                placeholder="Column name..."
                @keyup.enter="addContextColumn"
              />
              <button
                @click="addContextColumn"
                class="px-3 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200"
              >Add</button>
            </div>
            <p class="text-xs text-gray-500 mt-1">Columns available to DataAttributes (e.g., observation_date, discharge_date)</p>
          </div>

          <!-- SQL Query -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">SQL Query</label>
            <textarea
              v-model="form.sql_query"
              rows="12"
              :disabled="selectedPopulation?.is_builtin"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono disabled:bg-gray-100"
              placeholder="SELECT unit_key, context_columns FROM ..."
            ></textarea>
            <p class="text-xs text-gray-500 mt-1">
              Must return <code class="bg-gray-100 px-1">unit_key</code> + context columns.
              Use placeholders: <code class="bg-gray-100 px-1">{tenant_id}</code>, <code class="bg-gray-100 px-1">{start_date}</code>, <code class="bg-gray-100 px-1">{end_date}</code>
            </p>
          </div>

          <!-- Test SQL -->
          <div v-if="!selectedPopulation?.is_builtin" class="border-t border-gray-200 pt-4">
            <button
              @click="testSql"
              :disabled="testing || !form.sql_query"
              class="px-4 py-2 bg-gray-100 rounded text-sm hover:bg-gray-200 disabled:opacity-50"
            >
              {{ testing ? 'Testing...' : 'Test SQL' }}
            </button>
            <div v-if="testResult" class="mt-2 p-3 rounded text-sm" :class="testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'">
              {{ testResult.message }}
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            @click="showEditor = false"
            class="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
          >
            {{ selectedPopulation?.is_builtin ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="!selectedPopulation?.is_builtin"
            @click="savePopulation"
            :disabled="saving || !form.name || !form.sql_query"
            class="btn-primary-sm"
          >
            {{ saving ? 'Saving...' : (selectedPopulation ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PopulationManager',
  data() {
    return {
      populations: [],
      loading: true,
      showEditor: false,
      selectedPopulation: null,
      saving: false,
      testing: false,
      testResult: null,
      newContextColumn: '',
      form: {
        name: '',
        description: '',
        unit_type: 'patient',
        context_columns: [],
        sql_query: '',
      },
    };
  },
  methods: {
    async fetchPopulations() {
      this.loading = true;
      try {
        const response = await axios.get('api/impact_analytics/populations/');
        this.populations = response.data;
      } catch (error) {
        console.error('Error fetching populations:', error);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        name: '',
        description: '',
        unit_type: 'patient',
        context_columns: [],
        sql_query: '',
      };
      this.testResult = null;
      this.newContextColumn = '';
    },
    editPopulation(pop) {
      this.selectedPopulation = pop;
      this.form = {
        name: pop.name,
        description: pop.description || '',
        unit_type: pop.unit_type,
        context_columns: [...(pop.context_columns || [])],
        sql_query: pop.sql_query || '',
      };
      this.testResult = null;
      this.showEditor = true;
    },
    addContextColumn() {
      const col = this.newContextColumn.trim();
      if (col && !this.form.context_columns.includes(col)) {
        this.form.context_columns.push(col);
        this.newContextColumn = '';
      }
    },
    removeContextColumn(col) {
      this.form.context_columns = this.form.context_columns.filter(c => c !== col);
    },
    async testSql() {
      this.testing = true;
      this.testResult = null;
      try {
        const response = await axios.post('api/impact_analytics/populations/test-sql/', {
          sql_query: this.form.sql_query,
        });
        this.testResult = {
          success: true,
          message: `Success! Returned ${response.data.row_count} rows. Columns: ${response.data.columns.join(', ')}`,
        };
      } catch (error) {
        this.testResult = {
          success: false,
          message: error.response?.data?.error || error.message,
        };
      } finally {
        this.testing = false;
      }
    },
    async savePopulation() {
      this.saving = true;
      try {
        if (this.selectedPopulation) {
          await axios.put(`api/impact_analytics/populations/${this.selectedPopulation.id}/`, this.form);
        } else {
          await axios.post('api/impact_analytics/populations/', this.form);
        }
        await this.fetchPopulations();
        this.showEditor = false;
      } catch (error) {
        console.error('Error saving population:', error);
        alert('Error: ' + (error.response?.data?.error || error.message));
      } finally {
        this.saving = false;
      }
    },
    async deletePopulation(pop) {
      if (!confirm(`Delete population "${pop.name}"?`)) return;
      try {
        await axios.delete(`api/impact_analytics/populations/${pop.id}/`);
        await this.fetchPopulations();
      } catch (error) {
        console.error('Error deleting population:', error);
        alert('Error: ' + (error.response?.data?.error || error.message));
      }
    },
  },
  mounted() {
    this.fetchPopulations();
  },
};
</script>
